import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from '../../config';
import { useCookies } from "react-cookie";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  HStack,
  Text,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FiPhone, FiUser } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import { colors, gradients, radii, shadows } from "./designSystem";

const LoginForm = ({
  onSuccess,
  onCancel,
  showLinks = true,
  showToast = true,
  mode = "page", // "page" | "modal"
  redirectPath = null,
}) => {
  const [, setCookie] = useCookies(["role"]);
  const [step, setStep] = useState("phone"); // phone | otp | name
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const timerRef = useRef(null);

  const startOtpTimer = () => {
    setOtpTimer(10 * 60); // 10 minutes in seconds
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const validateIndianMobile = (value) => /^[6-9]\d{9}$/.test(String(value || '').replace(/\s/g, ''));

  const resolveTarget = (role) => {
    if (redirectPath) return redirectPath;
    if (role === "patient") return "/home";
    if (role === "pharmacist") return "/pharmacist/home";
    if (role === "admin") return "/admin-home";
    return "/home";
  };

  const finishLogin = (role, message) => {
    if (role) {
      setCookie("role", role, { path: "/" });
    }
    if (showToast) toast.success(message, { position: "bottom-left" });
    if (onSuccess) {
      onSuccess({ role });
    } else {
      setTimeout(() => {
        window.location.replace(resolveTarget(role));
      }, 100);
    }
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault?.();
    if (!validateIndianMobile(phone)) {
      if (showToast) toast.error("Please enter a valid 10-digit Indian mobile number", { position: "bottom-left" });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/send-phone-otp`, { phone }, { withCredentials: true });
      if (data.success) {
        if (showToast) toast.success(data.message, { position: "bottom-left" });
        setStep("otp");
        startOtpTimer();
      } else {
        if (showToast) toast.error(data.message || "Failed to send OTP", { position: "bottom-left" });
      }
    } catch (err) {
      if (showToast) toast.error("Failed to send OTP", { position: "bottom-left" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e?.preventDefault?.();
    if (!otp || otp.length < 4) {
      if (showToast) toast.error("Please enter the OTP", { position: "bottom-left" });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/verify-phone-otp`,
        { phone, otp },
        { withCredentials: true }
      );
      if (!data.success) {
        if (showToast) toast.error(data.message || "Verification failed", { position: "bottom-left" });
        return;
      }
      if (data.isNewUser) {
        setStep("name");
      } else {
        finishLogin(data.role, data.message);
      }
    } catch (err) {
      if (showToast) toast.error("OTP verification failed", { position: "bottom-left" });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      if (showToast) toast.error("Please enter your name", { position: "bottom-left" });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/verify-phone-otp`,
        { phone, otp, name: name.trim() },
        { withCredentials: true }
      );
      if (data.success) {
        finishLogin(data.role, data.message);
      } else {
        if (showToast) toast.error(data.message || "Signup failed", { position: "bottom-left" });
      }
    } catch (err) {
      if (showToast) toast.error("Signup failed", { position: "bottom-left" });
    } finally {
      setLoading(false);
    }
  };

  const renderPhoneStep = () => (
    <Stack spacing="5" as="form" onSubmit={handleSendOTP}>
      <FormControl isRequired>
        <FormLabel htmlFor="phone" fontSize="13px" fontWeight="800" color={colors.neutral[700]}>
          Phone number
        </FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none" color={colors.neutral[400]}>
            <FiPhone />
          </InputLeftElement>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit Indian mobile number"
            bg={colors.neutral[50]}
            borderColor={colors.neutral[200]}
            borderRadius={radii.md}
            h="48px"
            _hover={{ borderColor: colors.brand[200] }}
            _focusVisible={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
          />
        </InputGroup>
      </FormControl>
      <Button
        isLoading={loading}
        bgGradient={gradients.brand}
        color="white"
        type="submit"
        h="50px"
        borderRadius={radii.md}
        fontWeight="800"
        boxShadow={shadows.brand}
        _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
      >
        Send OTP
      </Button>
    </Stack>
  );

  const renderOtpStep = () => (
    <Stack spacing="5" as="form" onSubmit={handleVerifyOTP}>
      <FormControl isRequired>
        <FormLabel htmlFor="otp" fontSize="13px" fontWeight="800" color={colors.neutral[700]}>
          Enter OTP
        </FormLabel>
        <Text fontSize="12px" color={colors.neutral[500]} mb={1}>
          A 6-digit OTP has been sent to your WhatsApp on <strong>+91 {phone}</strong>
        </Text>
        <Text fontSize="12px" color={otpTimer > 0 ? colors.neutral[500] : "red.500"} mb={2}>
          {otpTimer > 0
            ? `Valid for ${formatTimer(otpTimer)}`
            : "OTP has expired. Please request a new one."}
        </Text>
        <Input
          id="otp"
          name="otp"
          type="text"
          inputMode="numeric"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="6-digit OTP"
          bg={colors.neutral[50]}
          borderColor={colors.neutral[200]}
          borderRadius={radii.md}
          h="48px"
          _hover={{ borderColor: colors.brand[200] }}
          _focusVisible={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
        />
      </FormControl>
      <Button
        isLoading={loading}
        bgGradient={gradients.brand}
        color="white"
        type="submit"
        h="50px"
        borderRadius={radii.md}
        fontWeight="800"
        boxShadow={shadows.brand}
        _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
      >
        Verify & Continue
      </Button>
      <Button
        variant="ghost"
        size="sm"
        color={colors.brand[700]}
        fontWeight="800"
        onClick={() => setStep("phone")}
      >
        Change phone number
      </Button>
    </Stack>
  );

  const renderNameStep = () => (
    <Stack spacing="5" as="form" onSubmit={handleCompleteSignup}>
      <FormControl isRequired>
        <FormLabel htmlFor="name" fontSize="13px" fontWeight="800" color={colors.neutral[700]}>
          What's your name?
        </FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none" color={colors.neutral[400]}>
            <FiUser />
          </InputLeftElement>
          <Input
            id="name"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            bg={colors.neutral[50]}
            borderColor={colors.neutral[200]}
            borderRadius={radii.md}
            h="48px"
            _hover={{ borderColor: colors.brand[200] }}
            _focusVisible={{ borderColor: colors.brand[400], boxShadow: `0 0 0 1px ${colors.brand[400]}` }}
          />
        </InputGroup>
      </FormControl>
      <Button
        isLoading={loading}
        bgGradient={gradients.brand}
        color="white"
        type="submit"
        h="50px"
        borderRadius={radii.md}
        fontWeight="800"
        boxShadow={shadows.brand}
        _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
      >
        Create Account
      </Button>
    </Stack>
  );


  return (
    <Box>
      {step === "phone" && renderPhoneStep()}
      {step === "otp" && renderOtpStep()}
      {step === "name" && renderNameStep()}

      {showLinks && step === "phone" && (
        <Stack spacing="4" mt={6}>
          <HStack spacing={3}>
            <Divider borderColor={colors.neutral[200]} />
            <Text fontSize="12px" whiteSpace="nowrap" color={colors.neutral[500]} fontWeight="800" letterSpacing="0.08em" textTransform="uppercase">
              Staff access
            </Text>
            <Divider borderColor={colors.neutral[200]} />
          </HStack>
          <Stack spacing={2} align="stretch">
            <Text fontSize="13px" color={colors.neutral[500]} lineHeight="1.7" m={0}>
              Pharmacists and admins log in with username and password.
            </Text>
            <Button
              as={Link}
              to="/login"
              variant="outline"
              borderColor={colors.brand[200]}
              color={colors.brand[700]}
              fontWeight="800"
              borderRadius={radii.md}
              _hover={{ bg: colors.brand[50] }}
            >
              Staff login
            </Button>
          </Stack>
        </Stack>
      )}
      {mode === "modal" && onCancel && (
        <Button variant="ghost" onClick={onCancel} mt={4}>
          Cancel
        </Button>
      )}
      {showToast && <ToastContainer />}
    </Box>
  );
};

export default LoginForm;
